import { SPENDING_BAR_CLASSES, type SpendingReading } from '../../utils/spending';

/**
 * The spending bar (task 1159): billed usage against the monthly limit, in the
 * reading's tone (indigo, amber, red — see `utils/spending.ts`). The fill stops
 * at the bar's end past 100%; the percentage beside it keeps counting.
 *
 * `thresholdsPct` marks each configured alert threshold on the bar (the
 * Spending page); the compact variant on the Billing row and the Dashboard tile
 * leaves them out. Renders nothing without a limit.
 */
export default function SpendingBar({
  reading,
  thresholdsPct = [],
  compact = false,
  testId = 'spending-bar',
}: {
  reading: SpendingReading;
  thresholdsPct?: number[];
  compact?: boolean;
  testId?: string;
}) {
  if (!reading.hasLimit || reading.pct === null) return null;
  const fill = Math.min(100, Math.max(0, reading.pct));

  return (
    <div className={compact ? '' : 'pb-4'}>
      <div
        role="meter"
        aria-label="Usage cost against the monthly spending limit"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={fill}
        aria-valuetext={`${reading.pct}% of the monthly spending limit`}
        data-testid={testId}
        data-tone={reading.tone}
        className={`relative w-full rounded-full bg-gray-200 dark:bg-gray-700 ${compact ? 'h-1.5' : 'h-2.5'}`}
      >
        <div
          className={`h-full rounded-full ${SPENDING_BAR_CLASSES[reading.tone]}`}
          style={{ width: `${fill}%` }}
        />
        {thresholdsPct.map((pct) => (
          <div
            key={pct}
            data-testid={`${testId}-threshold-${pct}`}
            className="absolute top-[-3px] bottom-[-3px] w-px bg-gray-500 dark:bg-gray-400"
            style={{ left: `${Math.min(100, Math.max(0, pct))}%` }}
          >
            <span className="absolute top-full mt-0.5 -translate-x-1/2 text-[10px] text-gray-500 dark:text-gray-400 tabular-nums">
              {pct}%
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
